import { useCallback, useEffect, useRef, useState } from "react";
import {
  getClipSelectionMode,
  resolveClipSelection,
  toSelectionModifiers,
} from "./clipSelection";
import {
  getClipPackingLaneIndex,
  resolveTimelineGroupMoveEnd,
  resolveTimelineResizeEnd,
} from "./clipTimelineUtils";
import {
  getTimelineRowHeight,
  VIDEO_EDITOR_FPS,
  VIDEO_EDITOR_TIMELINE_AUTO_SCROLL_EDGE,
  VIDEO_EDITOR_TIMELINE_KEYBOARD_NUDGE_LARGE_SECONDS,
  VIDEO_EDITOR_TIMELINE_KEYBOARD_NUDGE_SECONDS,
  VIDEO_EDITOR_TIMELINE_LANE_HYSTERESIS_PX,
} from "./constants";
import { getTimelineRowsLayout } from "./timelineLayout";
import {
  CLIP_INTERACTION_MODE,
  collectSnapTargets,
  getClipDuration,
  getGhostGeometry,
  getPointerTimelineMetrics,
  getPreferredPackingLane,
  pointerToTime,
  resolveDropTarget,
  snapDragTime,
} from "./timelineInteractionUtils";

const SIGNATURE_NONE = "";

const getDropSignature = ({
  dropTarget,
  preferredLaneIndex,
  start,
  end,
  scrollTop = 0,
}) =>
  [
    dropTarget?.kind || "none",
    dropTarget?.placement || "",
    dropTarget?.trackId || "",
    preferredLaneIndex ?? "",
    Number(start || 0).toFixed(2),
    Number(end || 0).toFixed(2),
    Math.round(scrollTop),
  ].join("|");

const applyAutoScroll = ({ editRect, clientX, clientY, timelineRef }) => {
  if (!editRect || !timelineRef?.current) {
    return;
  }

  const api = timelineRef.current;
  const edge = VIDEO_EDITOR_TIMELINE_AUTO_SCROLL_EDGE;
  const root = api.target;
  const scrollNode =
    root?.querySelector?.(".timeline-editor-edit-area .ReactVirtualized__Grid") ||
    root?.querySelector?.(".timeline-editor-edit-area");
  const scrollLeft = scrollNode?.scrollLeft || 0;
  const scrollTop = scrollNode?.scrollTop || 0;

  let nextLeft = scrollLeft;
  let nextTop = scrollTop;

  if (clientX > editRect.right - edge) {
    nextLeft += 18;
  } else if (clientX < editRect.left + edge) {
    nextLeft -= 18;
  }

  if (clientY > editRect.bottom - edge) {
    nextTop += 14;
  } else if (clientY < editRect.top + edge) {
    nextTop -= 14;
  }

  if (nextLeft !== scrollLeft) {
    api.setScrollLeft?.(Math.max(0, nextLeft));
  }
  if (nextTop !== scrollTop) {
    api.setScrollTop?.(Math.max(0, nextTop));
  }
};

const formatTimeLabel = (time) => `${Math.max(0, time).toFixed(1)}s`;

const applyGhostNode = (node, geometry) => {
  if (!node || !geometry) {
    return;
  }
  node.style.transform = `translate3d(${geometry.left}px, ${geometry.top}px, 0)`;
  node.style.width = `${geometry.width}px`;
  node.style.height = `${geometry.height}px`;
};

const useTimelineClipInteraction = ({
  edl,
  playhead = 0,
  scaleWidth,
  timelineAreaRef,
  timelineRef,
  selectedClipId,
  selectedClipIds = [],
  onCommitEdl = () => {},
  onSelectClip = () => {},
}) => {
  const dragRef = useRef(null);
  const ghostNodeRef = useRef(null);
  const groupGhostRefs = useRef(new Map());
  const rafRef = useRef(null);
  const lastPointerRef = useRef({ x: 0, y: 0 });
  const lastLaneYRef = useRef(null);
  const signatureRef = useRef(SIGNATURE_NONE);
  const playheadRef = useRef(playhead);
  const scaleWidthRef = useRef(scaleWidth);
  const edlRef = useRef(edl);
  const selectedClipIdsRef = useRef(selectedClipIds);
  const [dragState, setDragState] = useState(null);

  playheadRef.current = playhead;
  scaleWidthRef.current = scaleWidth;
  edlRef.current = edl;
  selectedClipIdsRef.current = selectedClipIds;

  const stopRaf = () => {
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const resolveDragFrame = useCallback(() => {
    const drag = dragRef.current;
    const sourceEdl = drag?.sourceEdl;
    if (!drag || !sourceEdl) {
      return;
    }

    const pointer = lastPointerRef.current;
    const currentScaleWidth = scaleWidthRef.current;
    const metrics = getPointerTimelineMetrics({
      timelineAreaNode: timelineAreaRef.current,
      clientX: pointer.x,
      clientY: pointer.y,
    });

    applyAutoScroll({
      editRect: metrics.editRect,
      clientX: pointer.x,
      clientY: pointer.y,
      timelineRef,
    });

    const fps = sourceEdl.fps || VIDEO_EDITOR_FPS;
    const sourceClip = sourceEdl.clips.byId[drag.clipId];
    if (!sourceClip) {
      return;
    }

    const duration = getClipDuration(sourceClip);
    const pointerTime = pointerToTime({
      xInContent: metrics.xInContent,
      scaleWidth: currentScaleWidth,
    });
    const snapTargets = collectSnapTargets({
      edl: sourceEdl,
      excludeClipIds: drag.clipIds,
      playhead: playheadRef.current,
    });

    let nextStart = drag.originStart;
    let nextEnd = drag.originStart + duration;
    let dir = "right";

    if (drag.mode === CLIP_INTERACTION_MODE.MOVE) {
      nextStart = snapDragTime({
        time: pointerTime - drag.grabOffsetTime,
        fps,
        scaleWidth: currentScaleWidth,
        snapTargets,
      });
      nextEnd = nextStart + duration;
    } else if (drag.mode === CLIP_INTERACTION_MODE.RESIZE_LEFT) {
      dir = "left";
      nextStart = snapDragTime({
        time: pointerTime,
        fps,
        scaleWidth: currentScaleWidth,
        snapTargets,
      });
      nextStart = Math.min(nextStart, nextEnd - 0.1);
    } else {
      nextEnd = snapDragTime({
        time: pointerTime,
        fps,
        scaleWidth: currentScaleWidth,
        snapTargets,
      });
      nextEnd = Math.max(nextEnd, nextStart + 0.1);
    }

    const layout = getTimelineRowsLayout({ edl: sourceEdl });
    let targetRow = layout.rows.find(
      (row) =>
        metrics.offsetY >= row.top &&
        metrics.offsetY < row.top + row.slotHeight
    );

    if (!targetRow) {
      if (metrics.offsetY < (layout.rows[0]?.top || 0)) {
        targetRow = layout.rows[0] || null;
      } else {
        targetRow = layout.rows[layout.rows.length - 1] || null;
      }
    }

    if (
      lastLaneYRef.current != null &&
      targetRow &&
      Math.abs(metrics.offsetY - lastLaneYRef.current) <
        VIDEO_EDITOR_TIMELINE_LANE_HYSTERESIS_PX &&
      drag.lastTargetRowId &&
      drag.lastTargetRowId !== targetRow.id
    ) {
      targetRow =
        layout.rows.find((row) => row.id === drag.lastTargetRowId) ||
        layout.rows.find(
          (row) => Math.abs(row.top - (drag.lastTargetRowTop || 0)) < 2
        ) ||
        targetRow;
    } else if (targetRow) {
      lastLaneYRef.current = targetRow.top + targetRow.slotHeight / 2;
      drag.lastTargetRowId = targetRow.id;
      drag.lastTargetRowTop = targetRow.top;
    }

    const dropTarget = resolveDropTarget({
      clip: sourceClip,
      targetRow,
    });

    const preferredLaneIndex = getPreferredPackingLane({
      edl: sourceEdl,
      clip: sourceClip,
      targetRow,
    });

    const deltaTime = nextStart - drag.originStart;
    const primaryGeometry = getGhostGeometry({
      start: nextStart,
      duration: nextEnd - nextStart,
      row: targetRow,
      scaleWidth: currentScaleWidth,
      scrollLeft: metrics.scrollLeft,
      scrollTop: metrics.scrollTop,
    });

    if (drag.mode === CLIP_INTERACTION_MODE.MOVE) {
      primaryGeometry.top =
        pointer.y - (metrics.areaRect?.top || 0) - drag.grabOffsetY;
      primaryGeometry.height = getTimelineRowHeight({
        type: sourceClip.type,
      });
    }

    applyGhostNode(ghostNodeRef.current, primaryGeometry);

    const groupGeometries = (drag.originClips || [])
      .filter((item) => item.clipId !== drag.clipId)
      .map((item) => {
        const geometry = getGhostGeometry({
          start: item.originStart + deltaTime,
          duration: item.duration,
          row: item.row,
          scaleWidth: currentScaleWidth,
          scrollLeft: metrics.scrollLeft,
          scrollTop: metrics.scrollTop,
        });
        if (drag.mode === CLIP_INTERACTION_MODE.MOVE) {
          geometry.top += primaryGeometry.top - (drag.originPrimaryTop || 0);
        }
        applyGhostNode(groupGhostRefs.current.get(item.clipId), geometry);
        return { ...item, geometry };
      });

    const signature = getDropSignature({
      dropTarget,
      preferredLaneIndex,
      start: nextStart,
      end: nextEnd,
      scrollTop: metrics.scrollTop,
    });

    drag.nextStart = nextStart;
    drag.nextEnd = nextEnd;
    drag.dropTarget = dropTarget;
    drag.preferredLaneIndex = preferredLaneIndex;
    drag.deltaTime = deltaTime;
    drag.targetRow = dropTarget.kind === "none" ? drag.sourceRow : targetRow;
    drag.dir = dir;

    if (signature === signatureRef.current) {
      return;
    }

    signatureRef.current = signature;
    setDragState({
      clipId: drag.clipId,
      clipIds: drag.clipIds,
      mode: drag.mode,
      dropTarget,
      tooltip: formatTimeLabel(nextStart),
      durationLabel: formatTimeLabel(nextEnd - nextStart),
      geometry: primaryGeometry,
      groupGeometries,
      label: sourceClip.label || sourceClip.type,
      type: sourceClip.type,
      sourceUrl: sourceClip.sourceUrl,
    });
  }, [timelineAreaRef, timelineRef]);

  const onClipPointerDown = useCallback(
    (event, { clipId, mode = CLIP_INTERACTION_MODE.MOVE }) => {
      const currentEdl = edlRef.current;
      if (!currentEdl || event.button === 2) {
        return;
      }

      const clip = currentEdl.clips.byId[clipId];
      if (!clip) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      const modifiers = toSelectionModifiers(event);
      const selectionMode = getClipSelectionMode(modifiers);
      const preserveMulti = mode === CLIP_INTERACTION_MODE.MOVE;
      onSelectClip({
        clipId,
        event: modifiers,
        preserveMulti,
      });

      const nextSelection = resolveClipSelection({
        currentIds: selectedClipIdsRef.current,
        clipId,
        event: modifiers,
        mode: selectionMode,
        preserveMulti,
      });
      const movingIds =
        mode === CLIP_INTERACTION_MODE.MOVE && nextSelection.selectedClipIds.length
          ? nextSelection.selectedClipIds
          : [clipId];

      const metrics = getPointerTimelineMetrics({
        timelineAreaNode: timelineAreaRef.current,
        clientX: event.clientX,
        clientY: event.clientY,
      });
      const layout = getTimelineRowsLayout({ edl: currentEdl });
      const sourceRow =
        layout.rows.find((row) => row.trackId === clip.trackId) ||
        layout.rows.find((row) => row.type === clip.type);
      const originStart = Number(clip.position) || 0;
      const pointerTime = pointerToTime({
        xInContent: metrics.xInContent,
        scaleWidth: scaleWidthRef.current,
      });
      const originPrimaryTop = sourceRow
        ? sourceRow.top - metrics.scrollTop
        : 0;

      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      lastLaneYRef.current = sourceRow
        ? sourceRow.top + sourceRow.slotHeight / 2
        : null;
      signatureRef.current = SIGNATURE_NONE;

      dragRef.current = {
        clipId,
        clipIds: movingIds,
        mode,
        pointerId: event.pointerId,
        sourceEdl: currentEdl,
        sourceRow,
        originStart,
        originX: event.clientX,
        originY: event.clientY,
        originPrimaryTop,
        hasMoved: false,
        grabOffsetTime: pointerTime - originStart,
        grabOffsetY:
          event.clientY - event.currentTarget.getBoundingClientRect().top,
        lastTargetRowTop: sourceRow?.top,
        preferredLaneIndex: getClipPackingLaneIndex({
          edl: currentEdl,
          clipId,
        }),
        originClips: movingIds.map((id) => {
          const item = currentEdl.clips.byId[id];
          const row =
            layout.rows.find((entry) => entry.trackId === item?.trackId) ||
            layout.rows.find((entry) => entry.type === item?.type);
          return {
            clipId: id,
            originStart: Number(item?.position) || 0,
            duration: getClipDuration(item),
            type: item?.type,
            sourceUrl: item?.sourceUrl,
            label: item?.label || item?.type,
            row,
          };
        }),
      };
    },
    [onSelectClip, timelineAreaRef]
  );

  const finishDrag = useCallback(
    ({ cancelled = false } = {}) => {
      const drag = dragRef.current;
      if (drag && !cancelled && drag.hasMoved && drag.nextStart == null) {
        resolveDragFrame();
      }
      const finished = dragRef.current;
      stopRaf();
      dragRef.current = null;
      signatureRef.current = SIGNATURE_NONE;
      lastLaneYRef.current = null;
      setDragState(null);

      if (!finished || cancelled || !finished.hasMoved) {
        return;
      }

      if (finished.mode === CLIP_INTERACTION_MODE.MOVE) {
        onCommitEdl({
          edl: resolveTimelineGroupMoveEnd({
            edl: finished.sourceEdl,
            clipIds: finished.clipIds,
            primaryClipId: finished.clipId,
            deltaTime: finished.deltaTime || 0,
            preferredLaneIndex: finished.preferredLaneIndex,
          }),
        });
        return;
      }

      onCommitEdl({
        edl: resolveTimelineResizeEnd({
          edl: finished.sourceEdl,
          clipId: finished.clipId,
          start: finished.nextStart,
          end: finished.nextEnd,
          dir: finished.dir,
        }),
      });
    },
    [onCommitEdl, resolveDragFrame]
  );

  useEffect(() => {
    const onPointerMove = (event) => {
      if (!dragRef.current) {
        return;
      }
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      const drag = dragRef.current;
      if (!drag.hasMoved) {
        const distance = Math.hypot(
          event.clientX - drag.originX,
          event.clientY - drag.originY
        );
        if (distance < 4) {
          return;
        }
        drag.hasMoved = true;
      }
      if (rafRef.current) {
        return;
      }
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        resolveDragFrame();
      });
    };

    const onPointerUp = () => {
      if (!dragRef.current) {
        return;
      }
      finishDrag({ cancelled: false });
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape" && dragRef.current) {
        event.preventDefault();
        finishDrag({ cancelled: true });
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      window.removeEventListener("keydown", onKeyDown);
      stopRaf();
    };
  }, [finishDrag, resolveDragFrame]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (dragRef.current || !selectedClipId || !edl) {
        return;
      }

      const target = event.target;
      if (target?.closest?.("input, textarea, [contenteditable='true']")) {
        return;
      }

      const large = event.shiftKey;
      const nudge = large
        ? VIDEO_EDITOR_TIMELINE_KEYBOARD_NUDGE_LARGE_SECONDS
        : VIDEO_EDITOR_TIMELINE_KEYBOARD_NUDGE_SECONDS;
      const movingIds = selectedClipIds?.length ? selectedClipIds : [selectedClipId];
      const primary = edl.clips.byId[selectedClipId];
      if (!primary) {
        return;
      }

      let delta = 0;
      if (event.key === "ArrowLeft") {
        delta = -nudge;
      } else if (event.key === "ArrowRight") {
        delta = nudge;
      } else {
        return;
      }

      event.preventDefault();
      onCommitEdl({
        edl: resolveTimelineGroupMoveEnd({
          edl,
          clipIds: movingIds,
          primaryClipId: selectedClipId,
          deltaTime: delta,
          preferredLaneIndex: getClipPackingLaneIndex({
            edl,
            clipId: selectedClipId,
          }),
        }),
      });
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [edl, onCommitEdl, selectedClipId, selectedClipIds]);

  return {
    displayEdl: edl,
    dragState,
    draggingClipId: dragState?.clipId || null,
    draggingClipIds: dragState?.clipIds || [],
    ghostNodeRef,
    groupGhostRefs,
    isDragging: Boolean(dragState),
    onClipPointerDown,
    highlightedTrackId: dragState?.dropTarget?.trackId || null,
  };
};

export default useTimelineClipInteraction;
